import { z } from 'zod';
import { Vehicle } from '../Vehicles/Vehicle';

export const AutodispatchOutputSchema__DEPRECATED = z.object({
    assigned: z
        .record(z.coerce.string(), z.array(z.coerce.string()))
        .describe('assigned vehicles with assignments'),
    unassigned: z.array(z.coerce.string()).describe('unassigned assignments'),
});
export type AutodispatchOutputType__DEPRECATED = z.infer<
    typeof AutodispatchOutputSchema__DEPRECATED
>;

export const SOLVER_TYPE = {
    INTERNAL: 'internal',
    EXTERNAL: 'external',
};

export const VEHICLE_MODE = {
    SINGLE: 'single',
    MULTI: 'multi',
};

export const DEPOT_MODE = {
    SINGLE: 'single',
    MULTI: 'multi',
};

export const CONSTRAINTS = {
    percentage_change_capacity_kg: { min: -100, max: 300, default: 0 },
    percentage_change_capacity_packages: { min: -100, max: 300, default: 0 },
    percentage_change_warehouse_working_hours: { min: -100, max: 300, default: 0 },
    service_time_on_stop: { min: 0, max: 10 * 60, default: null }, // seconds
    service_time_per_package: { min: 0, max: 10 * 60, default: null }, // seconds
    // cost_per_km: { min: 0, max: 10, default: null },
    percentage_change_cost_per_km: { min: -100, max: 300, default: 0 },
    // cost_per_minute: { min: 0, max: 10, default: null },
    percentage_change_cost_per_minute: { min: -100, max: 300, default: 0 },
    max_vehicle_capacity_packages: { min: 1, max: 1000, default: 100 },
    max_vehicle_capacity_kg: { min: 1, max: 2000, default: 1300 },
    warehouse_work_time_minutes: { min: 1, max: 24 * 60, default: 8 * 60 }, // minutes
    solver_type: { default: SOLVER_TYPE.EXTERNAL },
    percentage_change_service_time_on_stop: { min: -100, max: 300, default: 0 },
    percentage_change_service_time_per_package: { min: -100, max: 300, default: 0 },
    duration_seconds: { min: 3, max: 3600, default: 60 }, // seconds
    capacity_type: { default: 'packages' }, // 'packages' or 'kg'
    fleet_fixed: { default: true },
    options_layers_vehicleLimit_enabled: { default: true },
    vehicle_mode: { default: VEHICLE_MODE.SINGLE }, // 'single' or 'multi'
    depot_mode: { default: DEPOT_MODE.MULTI }, // 'single' or 'multi'
    special_dedicated_fleet_46963: { default: false },
    grouping_enabled: { default: false },
    keep_debug_data: { default: false },
    time_windows_jobs_enabled: { default: true },
    get_jobs_only_for_selected_day: { default: true },
    selected_day: { default: null },
    percentage_change_end_of_time_window: { min: -100, max: 300, default: 0 },
    use_truck_params: { default: false },
};

export const NucleiSchema = z.object({
    vehicle_id: z.number().describe('vehicle id'),
    coordinates: z.object({
        x: z.number().describe(''),
        y: z.number().describe(''),
    }),
});
export type NucleiType = z.infer<typeof NucleiSchema>;

export const AppOptionsSchema = z.object({
    vehicle_ids: z.array(z.number()).nullable().default(null),
    group_and_vehicle_ids: z.array(z.number()).nullable().default(null),
    min_vehicles: z.number().nullable().default(null).describe('Minimum number of vehicles to use'),
    max_vehicles: z.number().nullable().default(null).describe('Maximum number of vehicles to use'),
    nuclei: z.array(NucleiSchema),
    start_time_from_warehouse: z
        .string()
        .catch('08:00')
        .describe('Start time from warehouse in hh:mm format'),
    percentage_change_capacity_kg: z
        .number()
        .min(CONSTRAINTS.percentage_change_capacity_kg.min)
        .max(CONSTRAINTS.percentage_change_capacity_kg.max)
        .catch(CONSTRAINTS.percentage_change_capacity_kg.default)
        .describe('Percentage change of capacity in kg'),
    percentage_change_capacity_packages: z
        .number()
        .min(CONSTRAINTS.percentage_change_capacity_packages.min)
        .max(CONSTRAINTS.percentage_change_capacity_packages.max)
        .catch(CONSTRAINTS.percentage_change_capacity_packages.default)
        .describe('Percentage change of capacity in packages'),
    percentage_change_warehouse_working_hours: z
        .number()
        .min(CONSTRAINTS.percentage_change_warehouse_working_hours.min)
        .max(CONSTRAINTS.percentage_change_warehouse_working_hours.max)
        .catch(CONSTRAINTS.percentage_change_warehouse_working_hours.default)
        .describe('Percentage change of working hours'),
    service_time_on_stop: z
        .number()
        .min(CONSTRAINTS.service_time_on_stop.min)
        .max(CONSTRAINTS.service_time_on_stop.max)
        .nullable()
        .catch(CONSTRAINTS.service_time_on_stop.default)
        .describe('Service time on stop in minutes'),
    service_time_per_package: z
        .number()
        .min(CONSTRAINTS.service_time_per_package.min)
        .max(CONSTRAINTS.service_time_per_package.max)
        .nullable()
        .catch(CONSTRAINTS.service_time_per_package.default)
        .describe('Service time per package in minutes'),
    cost_per_km: z.number().nullable().catch(null).describe('Cost per km in PLN'),
    cost_per_minute: z.number().nullable().catch(null).describe('Cost per minute in PLN'),
    cost_per_item: z.number().nullable().catch(null).describe('Cost per item in PLN'),
    percentage_change_cost_per_km: z
        .number()
        .min(CONSTRAINTS.percentage_change_cost_per_km.min)
        .max(CONSTRAINTS.percentage_change_cost_per_km.max)
        .catch(CONSTRAINTS.percentage_change_cost_per_km.default)
        .describe('Percentage change of cost per km'),
    percentage_change_cost_per_minute: z
        .number()
        .min(CONSTRAINTS.percentage_change_cost_per_minute.min)
        .max(CONSTRAINTS.percentage_change_cost_per_minute.max)
        .catch(CONSTRAINTS.percentage_change_cost_per_minute.default)
        .describe('Percentage change of cost per minute'),
    max_vehicle_capacity_packages: z
        .number()
        .min(CONSTRAINTS.max_vehicle_capacity_packages.min)
        .max(CONSTRAINTS.max_vehicle_capacity_packages.max)
        .catch(CONSTRAINTS.max_vehicle_capacity_packages.default)
        .describe('Maximum vehicle capacity in packages'),
    max_vehicle_capacity_kg: z
        .number()
        .min(CONSTRAINTS.max_vehicle_capacity_kg.min)
        .max(CONSTRAINTS.max_vehicle_capacity_kg.max)
        .catch(CONSTRAINTS.max_vehicle_capacity_kg.default)
        .describe('Maximum vehicle capacity in kg'),
    warehouse_work_time_minutes: z
        .number()
        .min(CONSTRAINTS.warehouse_work_time_minutes.min)
        .max(CONSTRAINTS.warehouse_work_time_minutes.max)
        .catch(CONSTRAINTS.warehouse_work_time_minutes.default)
        .describe('Warehouse work time in minutes'),
    solver_type: z.enum([SOLVER_TYPE.INTERNAL, SOLVER_TYPE.EXTERNAL]),
    percentage_change_service_time_on_stop: z
        .number()
        .min(CONSTRAINTS.percentage_change_service_time_on_stop.min)
        .max(CONSTRAINTS.percentage_change_service_time_on_stop.max)
        .catch(CONSTRAINTS.percentage_change_service_time_on_stop.default)
        .describe('Percentage change of service time on stop'),
    percentage_change_service_time_per_package: z
        .number()
        .min(CONSTRAINTS.percentage_change_service_time_per_package.min)
        .max(CONSTRAINTS.percentage_change_service_time_per_package.max)
        .catch(CONSTRAINTS.percentage_change_service_time_per_package.default)
        .describe('Percentage change of service time per package'),
    enable_merging_jobs_while_solving: z
        .boolean()
        .default(false)
        .describe('Enable merging jobs while solving'),
    duration_seconds: z
        .number()
        .min(CONSTRAINTS.duration_seconds.min)
        .max(CONSTRAINTS.duration_seconds.max)
        .catch(CONSTRAINTS.duration_seconds.default)
        .describe('Duration of the calculation in seconds'),
    capacity_type: z
        .string()
        .catch(CONSTRAINTS.capacity_type.default)
        .describe('Type of capacity used for calculations'),
    fleet_fixed: z
        .boolean()
        .default(CONSTRAINTS.fleet_fixed.default)
        .describe('Is the fleet fixed?'),
    options_layers_vehicleLimit_enabled: z
        .boolean()
        .default(CONSTRAINTS.options_layers_vehicleLimit_enabled.default)
        .describe('Is the vehicle limit layer enabled?'),
    vehicle_mode: z
        .union([z.literal(VEHICLE_MODE.MULTI), z.literal(VEHICLE_MODE.SINGLE)])
        .default(CONSTRAINTS.vehicle_mode.default)
        .describe('Vehicle mode'),
    depot_mode: z
        .union([z.literal(DEPOT_MODE.MULTI), z.literal(DEPOT_MODE.SINGLE)])
        .default(CONSTRAINTS.depot_mode.default)
        .describe('Depot mode'),
    special_dedicated_fleet_46963: z
        .boolean()
        .default(CONSTRAINTS.special_dedicated_fleet_46963.default)
        .describe('Special dedicated fleet for 46963 - https://er.automapa.pl/issues/46963'),
    grouping_enabled: z
        .boolean()
        .default(CONSTRAINTS.grouping_enabled.default)
        .describe('Enable grouping of orders (if available)'),
    keep_debug_data: z
        .boolean()
        .default(CONSTRAINTS.keep_debug_data.default)
        .describe('Keep debug data from the solver (if available)'),
    time_windows_jobs_enabled: z
        .boolean()
        .catch(CONSTRAINTS.time_windows_jobs_enabled.default)
        .describe('Use jobs time windows'),
    get_jobs_only_for_selected_day: z
        .boolean()
        .default(CONSTRAINTS.get_jobs_only_for_selected_day.default),
    selected_day: z
        .string()
        .nullable()
        .default(null)
        .describe('Selected day for fetching jobs, format YYYY-MM-DD'),
    percentage_change_end_of_time_window: z
        .number()
        .min(CONSTRAINTS.percentage_change_end_of_time_window.min)
        .max(CONSTRAINTS.percentage_change_end_of_time_window.max)
        .catch(CONSTRAINTS.percentage_change_end_of_time_window.default)
        .describe('Percentage change of end of time window'),
    use_truck_params: z
        .boolean()
        .default(CONSTRAINTS.use_truck_params.default)
        .describe('Use truck parameters for calculations'),
});
export type AppOptionsType = z.infer<typeof AppOptionsSchema>;

export const AppOptionsDefault: AppOptionsType = {
    vehicle_ids: [],
    group_and_vehicle_ids: [],
    min_vehicles: 1,
    max_vehicles: 1,
    start_time_from_warehouse: '08:00',
    percentage_change_capacity_kg: CONSTRAINTS.percentage_change_capacity_kg.default,
    percentage_change_capacity_packages: CONSTRAINTS.percentage_change_capacity_packages.default,
    percentage_change_warehouse_working_hours:
        CONSTRAINTS.percentage_change_warehouse_working_hours.default,
    service_time_on_stop: CONSTRAINTS.service_time_on_stop.default,
    service_time_per_package: CONSTRAINTS.service_time_per_package.default,
    cost_per_km: null,
    cost_per_minute: null,
    cost_per_item: null,
    nuclei: [],
    max_vehicle_capacity_packages: CONSTRAINTS.max_vehicle_capacity_packages.default,
    max_vehicle_capacity_kg: CONSTRAINTS.max_vehicle_capacity_kg.default,
    warehouse_work_time_minutes: CONSTRAINTS.warehouse_work_time_minutes.default,
    solver_type: CONSTRAINTS.solver_type.default,
    percentage_change_cost_per_km: CONSTRAINTS.percentage_change_cost_per_km.default,
    percentage_change_cost_per_minute: CONSTRAINTS.percentage_change_cost_per_minute.default,
    percentage_change_service_time_on_stop:
        CONSTRAINTS.percentage_change_service_time_on_stop.default,
    percentage_change_service_time_per_package:
        CONSTRAINTS.percentage_change_service_time_per_package.default,
    enable_merging_jobs_while_solving: false,
    duration_seconds: CONSTRAINTS.duration_seconds.default,
    capacity_type: CONSTRAINTS.capacity_type.default,
    fleet_fixed: CONSTRAINTS.fleet_fixed.default,
    options_layers_vehicleLimit_enabled: CONSTRAINTS.options_layers_vehicleLimit_enabled.default,
    vehicle_mode: CONSTRAINTS.vehicle_mode.default,
    depot_mode: CONSTRAINTS.depot_mode.default,
    special_dedicated_fleet_46963: CONSTRAINTS.special_dedicated_fleet_46963.default,
    grouping_enabled: CONSTRAINTS.grouping_enabled.default,
    keep_debug_data: CONSTRAINTS.keep_debug_data.default,
    time_windows_jobs_enabled: CONSTRAINTS.time_windows_jobs_enabled.default,
    get_jobs_only_for_selected_day: CONSTRAINTS.get_jobs_only_for_selected_day.default,
    selected_day: CONSTRAINTS.selected_day.default,
    percentage_change_end_of_time_window: CONSTRAINTS.percentage_change_end_of_time_window.default,
    use_truck_params: CONSTRAINTS.use_truck_params.default,
};

export const PointToDrawSchema = z.object({
    id: z.coerce.number().describe('Point ID'),
    coords: z.array(z.number()).length(2).describe('Coordinates of the point'),
});
export type PointToDrawType = z.infer<typeof PointToDrawSchema>;

// Local copy to avoid circular dependency with EasyVrpCalculationApi.ts
const CalculationSummaryRouteSchema = z.object({
    vehicle_id: z.number().nullable().catch(null).describe('ID of the vehicle used in the route'),
    name: z.string().nullable().catch(null).describe('Name of the vehicle used in the route'),
    color: z.string().nullable().catch(null).describe('Color of the route for visualization'),
    time_minutes: z.number().nullable().catch(null).describe('Total time in minutes for the route'),
    time_stops_minutes: z
        .number()
        .nullable()
        .catch(null)
        .describe('Total stop time in minutes for the route'),
    time_driving_minutes: z
        .number()
        .nullable()
        .catch(null)
        .describe('Total driving time in minutes for the route'),
    distance_km: z
        .number()
        .nullable()
        .catch(null)
        .describe('Total distance in kilometers for the route'),
    package_count: z
        .number()
        .nullable()
        .catch(null)
        .describe('Number of packages handled in the route'),
    stop_count: z.number().nullable().catch(null).describe('Number of stops made in the route'),
    window_time_count: z
        .number()
        .nullable()
        .catch(null)
        .describe('Number of time windows in the route'),
    window_time_exceeded_minutes: z
        .number()
        .nullable()
        .catch(null)
        .describe('Total time exceeded for time windows in minutes'),
    time_story: z.array(z.string()).catch([]),
    max_capacity_kg: z
        .number()
        .nullable()
        .catch(null)
        .describe('Maximum capacity in kg for the route'),
    max_capacity_packages: z
        .number()
        .nullable()
        .catch(null)
        .describe('Maximum capacity in packages for the route'),
});

export const RouteToDrawSchema = z.object({
    // if missing, create from vehicleId and timestamp
    uniqueRouteId: z.string().describe('Unique route ID').nullable().catch(null),
    vehicleId: z.coerce.number().nullable().catch(null).describe('Vehicle ID'),
    color: z.string().describe('Color of the route').nullable().catch(null),
    name: z.string().describe('Name of the route').catch('missing-name'),
    route: z
        .array(z.array(z.number()).length(2))
        .describe('Route coordinates as array of [x, y] pairs'),
    points: z.array(PointToDrawSchema).describe('Points to draw on the route'),
    summary: CalculationSummaryRouteSchema,
});
export type RouteToDrawType = z.infer<typeof RouteToDrawSchema>;

export const MapContextSchema = z.object({
    extra_data: AppOptionsSchema,
    routesToDraw: z.array(RouteToDrawSchema),
    vehicles: z.array(z.instanceof(Vehicle)).describe('Vehicle[]'),
    selectedCalcId: z.coerce.number().nullable().catch(null).describe('Selected calculation ID'),
    selectedVehicleId: z.coerce.number().nullable().catch(null).describe('Selected vehicle ID'),
    selectedRouteId: z.string().nullable().catch(null).describe('Selected route ID'),
});
export type MapContextType = z.infer<typeof MapContextSchema>;
